import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  ViewChild,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import {
  AmbientLight,
  Box3,
  DirectionalLight,
  HemisphereLight,
  LinearSRGBColorSpace,
  MathUtils,
  Object3D,
  OrthographicCamera,
  PerspectiveCamera,
  Scene,
  Vector3,
  WebGLRenderer,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import RenderingContext from '../../../working-table/visualisation/rendering-context';
import { LoadingSpaceFactory } from '../../../working-table/visualisation/loading-space-factory';
import { LoadingSpaceType as LoadingSpaceTypeEnum } from 'src/app/core/services/import-export-service-types';
import { CargoSpace, Dimensions, Group, LoadingSpaceType, PalletInterlayer, PalletLayer, Session } from '../../../core/types';
import { Project } from '../../../core/objects/project';
import { ProjectToSessionMapper } from '../../../working-table/working-table.component';
import { LoadsGroupsDrawer } from '../../../working-table/visualisation/loads-groups-drawer';
import { LoadGroup } from '../../../working-table/visualisation/group';
import { Ground } from '../../../working-table/visualisation/ground';
import { Outside } from '../../../working-table/visualisation/outside';
import { LoadFactory } from '../../../working-table/visualisation/loadFactory';
import { sessionGroupsToLoadGroups } from '../../../working-table/visualisation/session-load-groups';
import { CookieService } from 'ngx-cookie-service';
import { convertGroupToCm, convertProjectDimensions } from '../../helpers/unitRecalculationHelpers';
import {
  buildPalletLayerAddPreviewGroups,
  expandLinkedPalletContentsForVehicle,
  getAllProjectCargoSpaces,
  isPalletContentsViewEnabled,
} from '../../helpers/pallet-loading-space.helper';

export function getProjectPreviewGroups(
  cargoSpaces: CargoSpace[] = [],
  allProjectCargoSpaces: CargoSpace[] = cargoSpaces,
  showPalletContents = false,
): Group[] {
  return cargoSpaces.flatMap((cargoSpace, index) =>
    expandLinkedPalletContentsForVehicle(
      cargoSpace,
      allProjectCargoSpaces,
      showPalletContents,
    ).map((group) => ({
      ...group,
      packed: index,
    })),
  );
}

@Component({
    selector: 'gl-project-preview',
    templateUrl: './project-preview.component.html',
    styleUrls: ['./project-preview.component.scss'],
    standalone: false
})
export class ProjectPreviewComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() project: Project;
  @Input() width: number;
  @Input() height: number;
  @Input() currentWorkspace = 0;
  @Input() cameraUpdate = false;
  @Input() currentStopIndex: number;
  @Input() is2Dimage = false;
  /** When set, preview shows committed pallet stacks plus these pending layers (add-load arrangement tab). */
  @Input() pendingPalletLayer: PalletLayer | null = null;
  @Input() pendingPalletLayersToAdd = 1;
  /** Lower, more side-on camera for single-layer arrangement preview only. */
  @Input() arrangementLayerPreviewCamera = false;
  @Input() pendingLoadColor: number;
  @Input() pendingLoadType = 0;
  @Input() pendingLoadName: string;
  @Input() pendingLoadWeight: number;
  @Input() pendingLoadDimensions: Dimensions;
  @Input() pendingInterlayer: PalletInterlayer | null = null;
  @ViewChild('projectPreviewContainer')
  public visualisationContainer: ElementRef<HTMLElement>;
  public loadingSpaceFactory: LoadingSpaceFactory = new LoadingSpaceFactory();
  private scene: Scene;
  private camera: PerspectiveCamera | OrthographicCamera;
  private renderer: WebGLRenderer;
  private controls: OrbitControls;
  private cameraFOV = 75;
  private renderingContext: RenderingContext<PerspectiveCamera | OrthographicCamera>;
  private loadingSpaceType: LoadingSpaceType;
  private session: Session;
  private wasRun = false;
  private animationID: number;
  private previewCaptureRafId?: number;
  private updatePreviewScheduled = false;
  private webglUnavailable = false;
  public previewImage: string = null;
  public is3D = true;

  constructor(
    private zone: NgZone,
    private cookieService: CookieService,
  ) {}

  private destroyPreview(): void {
    this.stopAnimation();
    this.cancelPreviewCapture();
    if (this.controls) {
      this.controls.dispose();
      this.controls = undefined;
    }
    if (this.renderer) {
      LoadFactory.clear();
      const canvas = this.renderer.domElement;
      this.renderer.dispose();
      this.renderer.forceContextLoss();
      canvas?.remove();
      this.renderer = undefined;
    }
    this.renderingContext = null;
    this.scene = undefined;
    this.camera = undefined;
  }

  private stopAnimation(): void {
    if (this.animationID != null) {
      cancelAnimationFrame(this.animationID);
      this.animationID = undefined;
    }
  }

  ngOnDestroy(): void {
    this.destroyPreview();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.webglUnavailable) {
      return;
    }
    const pendingInterlayerChanged =
      changes.pendingInterlayer &&
      !this.arePalletInterlayersEqual(
        changes.pendingInterlayer.previousValue,
        changes.pendingInterlayer.currentValue,
      );
    const pendingDimensionsChanged =
      changes.pendingLoadDimensions &&
      !this.areDimensionsEqual(
        changes.pendingLoadDimensions.previousValue,
        changes.pendingLoadDimensions.currentValue,
      );
    if (
      (changes.project &&
        changes.project.currentValue &&
        changes.project.currentValue.workSpaces.length > 0) ||
      changes.currentStopIndex?.currentValue >= 0 ||
      changes.pendingPalletLayer ||
      changes.pendingPalletLayersToAdd ||
      changes.arrangementLayerPreviewCamera ||
      changes.pendingLoadColor ||
      changes.pendingLoadType ||
      changes.pendingLoadName ||
      changes.pendingLoadWeight ||
      pendingDimensionsChanged ||
      pendingInterlayerChanged
    ) {
      this.scheduleUpdatePreview();
    }
  }

  private areDimensionsEqual(a?: Dimensions, b?: Dimensions): boolean {
    if (a === b) {
      return true;
    }
    if (!a || !b) {
      return !a && !b;
    }
    return a.width === b.width && a.length === b.length && a.height === b.height;
  }

  private arePalletInterlayersEqual(
    a: PalletInterlayer | null | undefined,
    b: PalletInterlayer | null | undefined,
  ): boolean {
    if (a === b) {
      return true;
    }
    if (!a || !b) {
      return !a && !b;
    }
    const da = a.dimensions;
    const db = b.dimensions;
    return (
      a.name === b.name &&
      a.color === b.color &&
      a.weight === b.weight &&
      a.regularity === b.regularity &&
      a.onTop === b.onTop &&
      a.onBottom === b.onBottom &&
      da?.width === db?.width &&
      da?.length === db?.length &&
      da?.height === db?.height
    );
  }

  private scheduleUpdatePreview(): void {
    if (this.updatePreviewScheduled) {
      return;
    }
    this.updatePreviewScheduled = true;
    requestAnimationFrame(() => {
      this.updatePreviewScheduled = false;
      this.updatePreview();
    });
  }

  private renderBmpImageFromScene(): string {
    this.previewImage = null;
    const canvas: HTMLCanvasElement = this.renderingContext?.renderer?.domElement;
    if (!canvas) {
      return '';
    } else {
      return canvas.toDataURL('image/png');
    }
    this.is3D = false;
  }

  public updatePreview() {
    if (this.webglUnavailable || !this.project?.workSpaces?.length) {
      return;
    }
    this.is3D = true;
    const isMm = this.project.options.unit === 'mm';
    const convertedProject = isMm ? convertProjectDimensions(this.project, false) : this.project;
    const workspaceCargoSpaces =
      this.currentStopIndex >= 0
        ? convertedProject.workSpaces[this.currentWorkspace].stops[this.currentStopIndex].cargoSpaces
        : convertedProject.workSpaces[this.currentWorkspace].cargoSpaces;
    const outsideSpace =
      this.currentStopIndex >= 0
        ? convertedProject.workSpaces[this.currentWorkspace].stops[this.currentStopIndex].outsideSpace
        : convertedProject.workSpaces[this.currentWorkspace].outsideSpace;
    const palletCargoSpace =
      workspaceCargoSpaces?.find((cargoSpace) => cargoSpace.type === LoadingSpaceTypeEnum.Pallet) ??
      workspaceCargoSpaces?.[0];
    const previewCargoSpaces =
      this.pendingPalletLayer && palletCargoSpace ? [palletCargoSpace] : workspaceCargoSpaces;
    this.session = new ProjectToSessionMapper(
      previewCargoSpaces,
      outsideSpace,
      isMm ? 'cm' : this.project.options.unit,
      undefined,
      getAllProjectCargoSpaces(convertedProject),
      isPalletContentsViewEnabled((name) => this.cookieService.get(name)),
    );
    this.loadingSpaceType = this.session.truck;
    if (this.visualisationContainer && this.project.workSpaces.length > 0) {
      if (!this.scene && !this.renderer) {
        this.initializePreview();
      }
      this.addObjects();
    }
  }

  private animate = () => {
    if (!this.renderer || !this.scene || !this.camera) {
      return;
    }
    this.renderer.render(this.scene, this.camera);
    this.controls?.update();
    this.animationID = requestAnimationFrame(this.animate);
  };

  private createWebGLRenderer(): WebGLRenderer | null {
    try {
      const renderer = new WebGLRenderer({
        antialias: true,
        alpha: true,
        preserveDrawingBuffer: true,
        powerPreference: 'low-power',
      });
      if (!renderer.getContext()) {
        renderer.dispose();
        return null;
      }
      return renderer;
    } catch {
      return null;
    }
  }

  private initializePreview(): void {
    if (this.webglUnavailable || this.renderer) {
      return;
    }
    this.previewImage = null;
    this.zone.runOutsideAngular(() => {
      this.scene = new Scene();
      this.camera = new PerspectiveCamera(
        this.cameraFOV,
        this.width / this.height,
        100,
        1000000000000,
      );

      this.renderer = this.createWebGLRenderer();
      if (!this.renderer) {
        this.webglUnavailable = true;
        this.is3D = false;
        return;
      }
      this.renderer.outputColorSpace = LinearSRGBColorSpace;
      this.renderer.setClearColor(0xffffff);
      this.renderer.setSize(this.width, this.height);
      this.visualisationContainer.nativeElement.appendChild(this.renderer.domElement);
      this.addGlobalLight();
      this.addDirectionalLight();
      this.initializeControls();
    });
  }

  private prepareGroup(groups: Group[]): LoadGroup[] {
    return sessionGroupsToLoadGroups(groups);
  }

  private addObjects(): void {
    if (this.loadingSpaceType === undefined || this.webglUnavailable || !this.renderer) {
      return;
    }
    this.stopAnimation();
    this.cancelPreviewCapture();
    if (this.scene) {
      this.scene.remove(...this.scene.children);
    } else {
      this.scene = new Scene();
    }
    this.addGlobalLight();
    this.addDirectionalLight();
    LoadFactory.clear();
    if (!this.renderingContext) {
      this.renderingContext = new RenderingContext(this.scene, this.renderer, this.camera);
    } else {
      this.renderingContext.scene = this.scene;
      this.renderingContext.camera = this.camera;
    }
    const loadingSpace = this.loadingSpaceType;
    const loadingSpaces = this.loadingSpaceFactory.createLoadingSpace(
      loadingSpace,
      this.project.options.unit,
    );
    loadingSpaces.draw(this.renderingContext);
    const ground = new Ground();
    const outside = new Outside(
      this.session.outside.dimensions,
      this.session.outside.position as Vector3,
    );
    ground.draw(this.scene, outside, this.session);
    const cargoSpaces =
      this.currentStopIndex >= 0
        ? this.project.workSpaces[this.currentWorkspace].stops[this.currentStopIndex].cargoSpaces
        : this.project.workSpaces[this.currentWorkspace].cargoSpaces;
    const palletCargoSpace =
      cargoSpaces?.find((cargoSpace) => cargoSpace.type === LoadingSpaceTypeEnum.Pallet) ??
      cargoSpaces?.[0];
    const groups =
      this.pendingPalletLayer && palletCargoSpace
        ? buildPalletLayerAddPreviewGroups({
            cargoSpace: palletCargoSpace,
            selectedLayer: this.pendingPalletLayer,
            layersToAdd: this.pendingPalletLayersToAdd,
            color: this.pendingLoadColor,
            loadType: this.pendingLoadType,
            name: this.pendingLoadName,
            weight: this.pendingLoadWeight,
            dimensions: this.pendingLoadDimensions,
            interlayer: this.pendingInterlayer,
          })
        : getProjectPreviewGroups(
            cargoSpaces,
            getAllProjectCargoSpaces(this.project),
            isPalletContentsViewEnabled((name) => this.cookieService.get(name)),
          );
    const isMm = this.project.options.unit === 'mm';
    const loadGroups = isMm
      ? this.prepareGroup(convertGroupToCm(groups))
      : this.prepareGroup(groups);
    const loadsGroupsDrawer = new LoadsGroupsDrawer(false, loadingSpaces);
    loadsGroupsDrawer.drawGroups(
      loadGroups,
      this.renderingContext.scene,
      this.loadingSpaceType.unit,
      false,
    );
    const unit = this.project.options.unit;
    const scalar = unit === 'cm' || unit === 'mm' ? 100 : 2.54;
    const totalHeight =
      unit === 'cm' || unit === 'mm'
        ? this.loadingSpaceType.height * scalar
        : this.loadingSpaceType.height * scalar * 50;
    this.scene.rotation.set(0, 0, 0);
    this.scene.position.set(0, 0, 0);
    this.scene.translateY(-totalHeight / 2);
    this.scene.rotation.y = MathUtils.degToRad(135);
    if (this.cameraUpdate || !this.wasRun) {
      this.setCamera(this.project.options.unit);
    }
    this.wasRun = true;
    this.animate();
    if (this.is2Dimage) {
      // Two rAFs: wait until the browser has painted the redrawn WebGL frame.
      this.previewCaptureRafId = requestAnimationFrame(() => {
        this.previewCaptureRafId = requestAnimationFrame(() => {
          this.previewImage = this.renderBmpImageFromScene();
          this.stopAnimation();
          this.previewCaptureRafId = undefined;
        });
      });
    }
  }

  private cancelPreviewCapture(): void {
    if (this.previewCaptureRafId != null) {
      cancelAnimationFrame(this.previewCaptureRafId);
      this.previewCaptureRafId = undefined;
    }
  }

  private setCamera(unit = 'cm'): void {
    const groupMix = new Object3D();
    groupMix.add(this.scene.getObjectByName('Basis').clone());
    const front = this.scene.getObjectByName('Front');
    if (front) {
      groupMix.add(this.scene.getObjectByName('Front').clone());
    }
    const setBasis = this.scene.getObjectByName('SetBasis');
    if (setBasis) {
      groupMix.add(this.scene.getObjectByName('SetBasis').clone());
    }
    const sceneBoundingBox = new Box3().setFromObject(groupMix);
    const sceneSize = sceneBoundingBox.getSize(new Vector3());
    const maxDimension = Math.max(sceneSize.x, sceneSize.y, sceneSize.z);
    const distance = maxDimension / (2 * Math.tan((this.cameraFOV * Math.PI) / 360));
    this.camera.lookAt(sceneBoundingBox.getCenter(new Vector3()));
    if (this.arrangementLayerPreviewCamera) {
      // Lower elevation, more side-on view so a single layer packing is readable.
      const center = sceneBoundingBox.getCenter(new Vector3());
      this.camera.position.set(
        center.x + distance * 0.55,
        center.y + sceneSize.y * 0.38,
        center.z + distance * 0.95,
      );
      this.camera.lookAt(center);
      this.camera.zoom = unit === 'cm' || unit === 'mm' ? 1.15 : 0.85;
    } else {
      this.camera.position.set(0, sceneSize.y, distance);
      this.camera.zoom = this.loadingSpaceType.type <= 2 ? 1.6 : 1;
      this.camera.zoom =
        this.loadingSpaceType.type === 4 || this.loadingSpaceType.type === 3 ? 0.8 : this.camera.zoom;
      this.camera.zoom = unit === 'cm' || unit === 'mm' ? this.camera.zoom : this.camera.zoom * 0.7;
    }
    this.camera.updateProjectionMatrix();
    this.camera.updateMatrix();
  }

  private addDirectionalLight(isFront = true): void {
    const directionalLight = new DirectionalLight(0xffffff, 0.5);
    directionalLight.position.set(isFront ? -25000 : 25000, 10000, -15000);
    directionalLight.name = isFront ? 'directionalLightFront' : 'directionalLightBack';
    this.scene.add(directionalLight);
  }

  private addGlobalLight(): void {
    const hemisphereLight = new HemisphereLight(0xffffff, 0xffffff, 2.0);
    hemisphereLight.name = 'hemiLight';
    this.scene.add(hemisphereLight);

    const ambientLight = new AmbientLight(0xffffff, 0.8);
    ambientLight.name = 'ambientLight';
    this.scene.add(ambientLight);
  }

  private initializeControls(): void {
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = false;
    this.controls.screenSpacePanning = false;
    this.controls.minDistance = 10;
    this.controls.maxDistance = 200000;
    this.controls.maxPolarAngle = Math.PI;
    this.controls.update();
  }

  ngAfterViewInit(): void {
    if (this.project?.workSpaces?.length > 0) {
      this.initializePreview();
      this.addObjects();
    }
  }
}
